import { useMemo } from "react";
import mergeWith from "lodash/mergeWith";

/**
 * Merge customiser that preserves identity for values lodash would otherwise destructively deep-clone.
 *
 * The customiser only intervenes for two object shapes that, while plain, must be passed by reference:
 *
 * - **React nodes**: anything carrying a `$$typeof` field (elements, portals, fragments, lazy, memo, etc.).
 * - **Refs**: `RefObject` / `MutableRefObject` shaped as a single-key
 *   `{ current }`. Cloning the wrapper detaches it from the ref React actually writes into.
 */
function preserveIdentity(_objValue: unknown, srcValue: unknown): unknown {
  if ((srcValue !== null || srcValue === undefined) && typeof srcValue === "object") {
    if ("$$typeof" in srcValue) return srcValue;
    const keys = Object.keys(srcValue);
    if (keys.length === 1 && keys[0] === "current") return srcValue;
  }
  return undefined; // fall through to default deep-merge
}

/**
 * Merges default properties with provided properties.
 *
 * Deep-merges plain configuration objects, but preserves identity for
 * React nodes and ref objects so that refs nested anywhere in the props
 * tree remain wired to React.
 *
 * @param defaultProps - The default properties to merge.
 * @param props - The properties to merge with the defaults.
 */
function useMergeProps<T, U>(defaultProps: T, props: U): T & U {
  return useMemo(() => mergeWith({}, defaultProps, props, preserveIdentity), [defaultProps, props]);
}

export default useMergeProps;
